'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown, LogOut, User, Settings } from 'lucide-react';
import { publicAuthorLogoutAction } from '@/app/actions/public-author-auth-actions';

type PublicAuthorMenuProps = {
  displayName: string;
  handle: string;
  avatarUrl?: string;
  email: string;
};

const PublicAuthorMenu = ({ displayName, handle, avatarUrl, email }: PublicAuthorMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, []);

  const initials =
    displayName
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase() || handle.slice(0, 2).toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-3 p-1 pl-3 pr-2 rounded-full border border-[var(--color-border)] bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] transition-all group"
      >
        <div className="text-right hidden sm:block">
          <div className="text-xs font-semibold text-[var(--color-text-main)]">
            {displayName}
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wider">
            @{handle}
          </div>
        </div>

        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg group-hover:shadow-primary/40 transition-shadow">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-full h-full object-cover rounded-full"
              loading="lazy"
            />
          ) : (
            <span className="text-xs font-bold text-white">{initials}</span>
          )}
        </div>

        <ChevronDown
          size={14}
          className={`text-[var(--color-text-muted)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-60 origin-top-right glass-card overflow-hidden shadow-2xl animate-fade-in z-50 border border-[var(--color-border)]">
          <div className="p-4 border-b border-[var(--color-border)] bg-[rgba(255,255,255,0.02)]">
            <p className="text-sm font-semibold text-[var(--color-text-main)]">
              {displayName}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] truncate">
              {email}
            </p>
          </div>

          <div className="p-2">
            <Link
              href={`/u/${handle}`}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <User size={16} />
              <span>View profile</span>
            </Link>

            {/*
            <Link
              href="/public-authors/dashboard"
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <PenSquare size={16} />
              <span>Author Studio</span>
            </Link>
            */}

            <Link
              href={`/u/${handle}/p`}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-main)] hover:bg-[rgba(255,255,255,0.05)] rounded-md transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <Settings size={16} />
              <span>Profile</span>
            </Link>
          </div>

          <div className="p-2 border-t border-[var(--color-border)] bg-[rgba(255,255,255,0.02)]">
            <form action={publicAuthorLogoutAction}>
              <button
                type="submit"
                className="flex w-full items-center gap-3 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors"
              >
                <LogOut size={16} />
                <span>Sign out</span>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicAuthorMenu;
